import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import DateControl from '../../components/Fields/DateControl';
import Field from '../../components/Fields/Field';
import FieldLabel from '../../components/Fields/FieldLabel';
import Form from '../../components/Fields/Form';
import SelectListField from '../../components/Fields/SelectListField';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import BillOfMaterialTemplateModel from '../../models/BillOfMaterialTemplateModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { Result } from '../../types/Result';
import FormatUtils from '../../utils/FormatUtils';
import Unauthorized from '../Authentication/Unauthorized';

const BillOfMaterialTemplateView: React.FC = () => {
    const [serverErrors, setServerErrors] = useState<Result>({ isSuccesful: true, messages: [] });
    const { handleResult, handleException } = useNotifications();
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);

    const [formData, setFormData] = useState<BillOfMaterialTemplateModel>({
        id: 0,
        name: '',
        itemTypeId: 0,
        beginDate: new FormatUtils().formatDateForStorage(new Date()),
        endDate: '',
        concurrencyId: '',
        billOfMaterialTemplateItems: []
    });

    const itemTypes = useSelectListOptions('api/v1/selectlist/ItemTypes', '-- Select item type');
    const categories = useSelectListOptions('api/v1/selectlist/ProductCategoryNameTrees', '-- Select category');

    let idParams: IdParams = useParams();
    let bomTemplateId: number | undefined;
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        bomTemplateId = parseInt(idParams.id);
    }

    useEffect(() => {
        if (bomTemplateId !== undefined) {
            DataService.getById<BillOfMaterialTemplateModel>('api/v1/billofmaterialtemplates/', bomTemplateId)
                .then(response => setFormData(response))
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [bomTemplateId]);

    function submit() {
        if (formData) {
            if (formData.id === 0)
                DataService
                    .create<BillOfMaterialTemplateModel>(`api/v1/billofmaterialtemplates/`, formData)
                    .then(response => {
                        handleResult(response);
                        setServerErrors(response);
                        if (response.isSuccesful === true && response.data)
                            setFormData(response.data);
                    })
                    .catch(e => handleException(e));
            else
                DataService
                    .update<BillOfMaterialTemplateModel>(`api/v1/billofmaterialtemplates/`, formData)
                    .then(response => {
                        handleResult(response);
                        setServerErrors(response);
                        if (response.isSuccesful === true && response.data)
                            setFormData(response.data);
                    })
                    .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'name', label: 'Name', validationRules: [{ type: 'isRequired' }] },
            { name: 'itemTypeId', label: 'Item type', validationRules: [{ type: 'isRequired' }] },
            { name: 'productCategoryId', label: 'Category'},
            { name: 'beginDate', label: 'Active from', validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
            { name: 'endDate', label: 'Active Until', labelSize: 'auto', validationRules: [{ type: 'isDate' }] },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    const { isAuthorizedFor } = useAuth();
    if (bomTemplateId === undefined) {
        if (!isAuthorizedFor(ApplicationFunctions.EditBomTemplate)) {
            return (<Unauthorized />);
        }
    }
    else if (!isAuthorized) {
        return (<Unauthorized />);
    }

    return (
        <div className='main-content'>
            <Form title='Bill-of-Material template' footer={(
                <Row>
                    <Col md='12'>
                        <ButtonBar offset={2}>
                            <Authorize applicationFunction={ApplicationFunctions.EditBomTemplate}>
                                <SaveButton  {...getSubmitButtonProps()} />
                            </Authorize>
                        </ButtonBar>
                    </Col>
                </Row>
            )}>
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <TextboxField {...getFieldProps('name')} />
                            <SelectListField {...getFieldProps('itemTypeId')} options={itemTypes} />
                            <SelectListField {...getFieldProps('productCategoryId')} options={categories} />
                            <Field {...getLabelProps('beginDate')}>
                                <DateControl {...getControlProps('beginDate')} />
                                <span style={{ padding: '7px 15px' }}><FieldLabel {...getLabelProps('endDate')} /></span>
                                <DateControl {...getControlProps('endDate')} />
                            </Field>
                        </Col>
                    </Row>
                </Container>
            </Form>
        </div>
    );
}
export default BillOfMaterialTemplateView;